import { Fragment, useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { css } from '@emotion/react';

import { useGetQueueGroupsByUser } from '../api/useQueueGroupController';
import { QueueProcessingCreateGroupPopup } from './QueueProcessingCreateGroupPopup';
import { IQueueGroup } from '../models/interfaces/IQueueGroup';
import { QueueItemStatus } from '../models/enums/QueueItemStatus';
import { QueueType } from '../models/enums/QueueType';
import { QUEUE_TYPE_META, STATUS_DISPLAY_ORDER, STATUS_META, StatusMeta } from '../utils/queueMeta';

const DEFAULT_PAGE_SIZE = 20;

const loadingStyle = css({
    display: 'flex',
    justifyContent: 'center',
    padding: 64,
});

const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    '& .group-list-sticky-top': {
        flexShrink: 0,
        backgroundColor: '#fff',
        padding: 8,
    },
    '& .group-list-header': {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
    },
    '& .group-list-toolbar': {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
    },
    '& .group-list-table-wrapper': {
        flex: 1,
        minHeight: 0,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    },
    '& .group-list-fetching-progress': {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        borderRadius: '8px 8px 0 0',
        height: 3,
    },
    '& .group-list-empty': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px',
        gap: 16,
        color: '#9e9e9e',
    },
    '& .group-list-pagination': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 12,
    },
    '& .status-counts-cell': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        minWidth: 220,
    },
    '& .status-badge': {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 3,
        paddingBottom: 3,
        borderRadius: 10,
        border: '1px solid transparent',
    },
});

function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
}

interface IQueueTypeBadgeProps {
    queueType: QueueType;
}

function QueueTypeBadge({ queueType }: IQueueTypeBadgeProps) {
    const meta = QUEUE_TYPE_META[queueType] ?? QUEUE_TYPE_META[QueueType.Unknown];

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
                backgroundColor: meta.bg,
                color: meta.color,
                border: `1px solid ${meta.color}33`,
            }}
        >
            <Typography variant='caption' sx={{ fontWeight: 600, color: 'inherit', lineHeight: 1 }}>
                {meta.label}
            </Typography>
        </Box>
    );
}

interface IStatusCountsCellProps {
    group: IQueueGroup;
}

function StatusCountsCell({ group }: IStatusCountsCellProps) {
    const { StatusCounts, TotalItems } = group;

    const visibleStatuses = STATUS_DISPLAY_ORDER.filter(
        (status) => (StatusCounts[status] ?? 0) > 0,
    );

    if (visibleStatuses.length === 0) {
        return (
            <Typography variant='caption' sx={{ color: '#bdbdbd' }}>
                No items
            </Typography>
        );
    }

    return (
        <Box className='status-counts-cell'>
            {visibleStatuses.map((status) => {
                const count = StatusCounts[status] ?? 0;
                const meta = STATUS_META[status];
                const pct = TotalItems > 0 ? Math.round((count / TotalItems) * 100) : 0;

                return (
                    <Tooltip
                        key={status}
                        title={`${meta.label}: ${count} of ${TotalItems} (${pct}%)`}
                        placement='top'
                    >
                        <Box
                            className='status-badge'
                            sx={{
                                backgroundColor: meta.bg,
                                borderColor: meta.borderColor,
                                color: meta.color,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: meta.color,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant='caption'
                                sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1 }}
                            >
                                {count}
                            </Typography>
                            <Typography
                                variant='caption'
                                sx={{ color: meta.color, opacity: 0.75, lineHeight: 1 }}
                            >
                                {meta.label}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

interface IQueueProcessingGroupListProps {
    onSelectGroup: (token: string) => void;
}

export function QueueProcessingGroupList({ onSelectGroup }: IQueueProcessingGroupListProps) {
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<IQueueGroup | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setPage(1);
        }, 400);

        return () => {
            if (debounceRef.current !== null) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [searchInput]);

    const { data, isLoading, isFetching, refetch } = useGetQueueGroupsByUser({
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        search: debouncedSearch,
    });

    if (isLoading) {
        return (
            <div css={loadingStyle}>
                <CircularProgress />
            </div>
        );
    }

    const groups = data?.Groups ?? [];
    const totalCount = data?.TotalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));

    return (
        <Fragment>
            <QueueProcessingCreateGroupPopup
                open={popupOpen}
                onChangeOpen={(open) => {
                    setPopupOpen(open);
                    if (!open) {
                        setEditingGroup(null);
                    }
                }}
                editGroup={editingGroup ? { token: editingGroup.Token, name: editingGroup.Name, queueType: editingGroup.QueueType, permission: editingGroup.Permission } : undefined}
                onSaved={(token) => {
                    if (!editingGroup) {
                        onSelectGroup(token);
                    }
                }}
            />
            <div css={containerStyle}>
                <div className='group-list-sticky-top'>
                    <div className='group-list-header'>
                        <AccountTreeOutlinedIcon sx={{ color: '#1565c0', fontSize: 28 }} />
                        <Typography variant='h6' sx={{ fontWeight: 700, color: '#212121' }}>
                            Batches
                        </Typography>
                        <Box
                            sx={{
                                ml: 1,
                                px: 1.5,
                                py: 0.25,
                                borderRadius: '12px',
                                backgroundColor: '#e3f2fd',
                                color: '#1565c0',
                            }}
                        >
                            <Typography variant='caption' sx={{ fontWeight: 700, color: 'inherit' }}>
                                {totalCount}
                            </Typography>
                        </Box>
                        <Button
                            variant='contained'
                            size='small'
                            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                                setEditingGroup(null);
                                setPopupOpen(true);
                            }}
                            sx={{ marginLeft: 'auto', textTransform: 'none', fontWeight: 600 }}
                        >
                            New Batch
                        </Button>
                        <Tooltip title='Refresh' placement='top'>
                            <span>
                                <IconButton
                                    size='small'
                                    onClick={() => refetch()}
                                    disabled={isFetching}
                                    aria-label='Refresh batches'
                                >
                                    <RefreshIcon
                                        sx={{
                                            fontSize: 20,
                                            color: '#1565c0',
                                            animation: isFetching ? 'spin 0.8s linear infinite' : 'none',
                                            '@keyframes spin': {
                                                from: { transform: 'rotate(0deg)' },
                                                to: { transform: 'rotate(360deg)' },
                                            },
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>

                    <div className='group-list-toolbar'>
                        <TextField
                            size='small'
                            placeholder='Search by group name...'
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 280 }}
                        />
                    </div>
                </div>

                <div className='group-list-table-wrapper'>
                    {isFetching && (
                        <LinearProgress
                            className='group-list-fetching-progress'
                            sx={{
                                backgroundColor: '#e3f2fd',
                                '& .MuiLinearProgress-bar': { backgroundColor: '#1565c0' },
                            }}
                        />
                    )}
                    {groups.length === 0 ? (
                        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <div className='group-list-empty'>
                                <AccountTreeOutlinedIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />
                                <Typography variant='body1' sx={{ color: '#9e9e9e' }}>
                                    No batches found
                                </Typography>
                                <Typography variant='body2' sx={{ color: '#bdbdbd' }}>
                                    {debouncedSearch
                                        ? 'Try a different search term'
                                        : 'Start an upload to create your first group'}
                                </Typography>
                            </div>
                        </Paper>
                    ) : (
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{ border: '1px solid #e0e0e0', borderRadius: 2, flex: 1, overflow: 'auto' }}
                        >
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            '& th': {
                                                fontWeight: 700,
                                                color: '#424242',
                                                fontSize: '0.8rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                borderBottom: '2px solid #e0e0e0',
                                                py: 1.5,
                                                backgroundColor: '#f8f9fa',
                                            },
                                        }}
                                    >
                                        <TableCell>Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Total</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Creator</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groups.map((group, index) => (
                                        <TableRow
                                            key={group.Token}
                                            onClick={() => onSelectGroup(group.Token)}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                                                '&:hover': { backgroundColor: '#f0f7ff' },
                                                transition: 'background-color 0.15s ease',
                                                '& td': {
                                                    borderBottom: '1px solid #f0f0f0',
                                                    py: 1.75,
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Typography
                                                    variant='body2'
                                                    sx={{ fontWeight: 600, color: '#212121' }}
                                                >
                                                    {group.Name || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <QueueTypeBadge queueType={group.QueueType} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant='body2'
                                                    sx={{ fontWeight: 600, color: '#424242' }}
                                                >
                                                    {group.TotalItems}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <StatusCountsCell group={group} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant='body2' sx={{ color: '#757575' }}>
                                                    {group.Creator || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant='body2' sx={{ color: '#757575' }}>
                                                    {formatDate(group.CreationDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align='right' sx={{ pr: 1 }}>
                                                <Button
                                                    size='small'
                                                    variant='outlined'
                                                    startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingGroup(group);
                                                        setPopupOpen(true);
                                                    }}
                                                    aria-label='Edit batch'
                                                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#1565c0', color: '#1565c0', '&:hover': { borderColor: '#0d47a1', backgroundColor: '#e3f2fd' } }}
                                                >
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </div>

                <div className='group-list-pagination'>
                    <Typography variant='body2' sx={{ color: '#757575' }}>
                        Page {page} of {totalPages}
                    </Typography>
                    <IconButton
                        size='small'
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || isFetching}
                        aria-label='Previous page'
                    >
                        <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                        size='small'
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages || isFetching}
                        aria-label='Next page'
                    >
                        <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </div>
            </div>
        </Fragment>
    );
}
